import { browserStorage } from '@/utils/browserStorage'
import { getDebouncer } from '@/utils/Debouncer'
import { getScrollbarWidth } from '@/utils/tools'
import { defineStore } from 'pinia'
import { computed, ref, StyleValue, watch } from 'vue'
import { useDefaultStore } from '.'
import { AvailableViews, ProductView } from '@/models/ProductsArchive'

export const useProductsViewStore = defineStore('products-view-store', () => {
  // -----------
  // -- Setup --
  // -----------
  const store = useDefaultStore()

  const resizeObserver = new ResizeObserver(
    getDebouncer(() => {
      updateMenuWidth()
    }, 100),
  )
  resizeObserver.observe(document.body)

  const storedView = browserStorage.get('productsArchive-view')

  // -----------------------
  // -- Reactives (state) --
  // -----------------------

  const view = ref<ProductView>(AvailableViews.includes(storedView) ? storedView : 'grid')

  const productSideMenuWidth = ref(0)
  const fullscreen = ref(false)

  const windowWidth = ref(window.innerWidth)
  const sideMenuCollapsed = ref(!!browserStorage.get('productsArchive-sideMenuCollapsed'))

  const stickyWrapperKey = ref(0)

  // ------------------------------
  // -- Computed props (getters) --
  // ------------------------------
  const isMobileWidth = computed(() => windowWidth.value < 768)
  const shouldBeCollapsed = computed(() => sideMenuCollapsed.value && !isMobileWidth.value)

  const stickyWrapperStyle = computed((): StyleValue => {
    stickyWrapperKey.value
    const leftMargin = fullscreen.value ? '0px' : `${productSideMenuWidth.value}px`
    return { width: `calc(100vw - ${getScrollbarWidth()}px - ${leftMargin})`, left: leftMargin }
  })

  // --------------
  // -- Watchers --
  // --------------
  watch(
    () => store.settings?.app.productsSideMenuWidth,
    () => updateMenuWidth(),
  )

  // -------------------------
  // -- Functions (actions) --
  // -------------------------
  const updateMenuWidth = () => {
    windowWidth.value = window.innerWidth

    if (isMobileWidth.value) {
      productSideMenuWidth.value = 0
    } else {
      const sideMenuWidth = store.settings?.app.productsSideMenuWidth || {
        default: 225,
        collapsed: 70,
      }
      productSideMenuWidth.value = sideMenuCollapsed.value ? sideMenuWidth.collapsed : sideMenuWidth.default
    }
    stickyWrapperKey.value++
  }

  const toggleMenuCollapsed = () => {
    sideMenuCollapsed.value = !sideMenuCollapsed.value
    updateMenuWidth()
    browserStorage.set('productsArchive-sideMenuCollapsed', sideMenuCollapsed.value)
  }

  return {
    view,
    fullscreen,
    productSideMenuWidth,

    isMobileWidth,
    shouldBeCollapsed,
    stickyWrapperStyle,

    toggleMenuCollapsed,
  }
})
